import snakeCase from 'lodash/snakeCase'

import { type TargetType } from 'components/attacks/create/create-attack-types'
import { generateAttackConfiguration } from 'components/attacks/create/create-attack-utils'
import {
  convertParamsToSets,
  getTotalSelectedClients
} from 'components/attacks/create/targets/target-utils'
import {
  type InfraImpactFormValues,
  type InfraTargetFormValues
} from 'components/attacks/create/utils/infra-initial-form-state'
import {
  isActive,
  isComplete
} from 'components/scenarios/utils/graph-node-lifecycle'
import {
  getGraphNodesByType,
  graphNodeToStepAttack
} from 'components/scenarios/utils/graph/legacy-graph'
import { isInfraAttackNode } from 'components/scenarios/utils/graph/nodes/typeguards'
import { trans } from 'i18n/translate'
import theme from 'theme'
import {
  type AttackCommand,
  type AttackConfiguration
} from 'types/api/attacks/details/targeting'
import { type K8sObject } from 'types/api/kubernetes/kubernetes'
import {
  type ScenarioRunResponse,
  ScenarioRunStage
} from 'types/api/scenarios/runs/scenario-runs'
import {
  type Scenario,
  ScenarioGraphNodeType,
  type StatusCheckNode
} from 'types/api/scenarios/scenario'
import { type Metadata } from 'types/metadata'
import { naturalSortBy, unique } from 'utils/list'
import { cleanObjKeysAndValues, keysToSnakeCase } from 'utils/obj'

import type { AttackSchedule } from 'types/api/schedules/active'
import type { ScenarioSchedule } from 'types/api/schedules/scenarios/scenario-schedule'

const countableNodeTypes = [
  ScenarioGraphNodeType.InfraAttack,
  ScenarioGraphNodeType.FailureFlag,
  ScenarioGraphNodeType.SynchronousStatusCheck
]

export const stepTypeLabels = {
  [ScenarioGraphNodeType.InfraAttack]: 'Experiment',
  [ScenarioGraphNodeType.ContinuousStatusCheck]: trans(
    'status_checks.continuous.label'
  ),
  [ScenarioGraphNodeType.SynchronousStatusCheck]: trans(
    'status_checks.label.singular'
  )
}

export function getScheduleMaxRuns(
  schedule: ScenarioSchedule | AttackSchedule
) {
  return schedule.trigger.maxRuns && Number(schedule.trigger.maxRuns)
}

export function getScenarioStepCount(scenario) {
  const countableNodes = getGraphNodesByType(
    scenario.graph,
    ...countableNodeTypes
  )
  return countableNodes.length
}

export function getCurrentStepForRun(run) {
  if (run) {
    const steps = getGraphNodesByType(run.graph, ...countableNodeTypes)
    const completedNodes = steps
      .map(
        (step) =>
          isActive(step.state?.lifecycle) || isComplete(step.state?.lifecycle)
      )
      .filter((successful) => successful === true)

    return completedNodes.length
  }
  return 0
}

export function getFirstAttackAsStep(scenario) {
  const attacks = getGraphNodesByType(
    scenario?.graph,
    ScenarioGraphNodeType.InfraAttack
  )
  if (!attacks.length) return
  return graphNodeToStepAttack(attacks[0])
}

export function getAttackFromStep(step, attackIndex = 0) {
  return step?.attacks[attackIndex]
}

export function getTaskFromSummary(summary) {
  if (!summary) return

  return Object.values(summary?.taskSummaries)[0]
}

export function scenarioEditedSinceRun(scenario, run) {
  if (!run) {
    return true
  }
  return run.scenario_edit_counter_at_run_time < scenario.edit_counter
}

export function getTargetTypeAndCommand(scenario) {
  const firstAttack = getFirstAttackAsStep(scenario)
  if (!firstAttack) return {}
  const command = firstAttack.impactDefinition.commandType
  const targetType = firstAttack.targetDefinition.targetType
  return { targetType, command }
}

export function getScenarioCommands(scenario): AttackCommand[] {
  return unique(
    getGraphNodesByType(scenario.graph, ScenarioGraphNodeType.InfraAttack).map(
      (node) => node.impact_definition.infra_command_args.cli_args[0]
    )
  )
}

export function getScenarioTargetTypes(scenario) {
  return unique(
    getGraphNodesByType(scenario.graph, ScenarioGraphNodeType.InfraAttack).map(
      (node) => node.target_definition.target_type
    )
  )
}

export function getBlastRadiusForStep(step) {
  const attack = getAttackFromStep(step)
  const isHost = 'Host' === attack?.targetDefinition?.targetType ?? 'Host'
  const blastRadiusLabel = attack.targetDefinition.strategy.percentage
    ? `${attack.targetDefinition.strategy.percentage}%`
    : attack.targetDefinition.strategy.count

  const blastRadiusValue = targetOverviewSubtitle(
    isHost,
    attack.targetDefinition.strategy
  )

  return {
    blastRadiusLabel,
    blastRadiusValue
  }
}

export function getBlastRadiusForNode(node) {
  const attack = node
  const isHost = 'Host' === attack?.target_definition?.target_type ?? 'Host'
  const strategy = attack.target_definition.strategy
  const blastRadiusLabel = strategy.percentage
    ? `${strategy.percentage}%`
    : strategy.count

  const blastRadiusValue = targetOverviewSubtitle(isHost, strategy)

  return {
    blastRadiusLabel,
    blastRadiusValue
  }
}

export function targetOverviewSubtitle(isHost, strategy) {
  const prefix = strategy.percentage ? 'of selected' : ''
  const suffix = strategy.percentage ? '' : 'selected'
  const type = isHost ? 'hosts' : 'containers'
  return `${prefix} ${type} ${suffix}`
}

export function targetCreationSubtitle(
  isHost,
  activeClients,
  selectedTargetParams,
  targetAll
) {
  const selectedTargetParamsSets = convertParamsToSets(selectedTargetParams)

  return `of ${getTotalSelectedClients(
    activeClients,
    'Random',
    isHost ? 'Hosts' : 'Containers',
    [],
    selectedTargetParamsSets,
    targetAll
  )}`
}

export const sortScenarioListDesc = (scenarios: Scenario[]) =>
  naturalSortBy(
    scenarios,
    ({ last_run_at, created_at }) => last_run_at || created_at,
    'desc'
  )

export const filterScenariosByCommands = (
  scenarios: Scenario[],
  search: string[] = []
) => {
  if (!search.length) return scenarios

  return scenarios.filter((scenario: Scenario) => {
    const { nodes } = scenario.graph ?? {}

    if (!nodes) return false

    return Object.values(nodes).some((node) => {
      if (!isInfraAttackNode(node)) return false

      const command = node.impact_definition?.infra_command_type?.toLowerCase()

      return command && search.includes(command)
    })
  })
}

export const filterScenariosByTechCategories = (
  scenarios: Scenario[],
  search: string[] = []
) => {
  if (!search.length) return scenarios

  return scenarios.filter((scenario: Scenario) => {
    const techCategory = scenario?.multi_select_tags?.techCategory ?? []

    return techCategory.some(
      (category) => category && search.includes(category)
    )
  })
}

export const filterScenarios = (scenarios: Scenario[], search: string) => {
  const lowercaseSearch = search.toLowerCase().trim()

  return scenarios.filter((scenario: Scenario) => {
    const matchesName = String(scenario.name)
      .toLowerCase()
      .includes(lowercaseSearch)

    if (matchesName) return true

    const command =
      Object.values(scenario.graph?.nodes ?? {}).map(
        (node) =>
          isInfraAttackNode(node) &&
          node.impact_definition?.infra_command_type?.toLowerCase()
      ) ?? []

    const matchesCommand = command.includes(lowercaseSearch)

    if (matchesCommand) return true

    const matchesAuthor = String(scenario.created_by)
      .toLowerCase()
      .includes(lowercaseSearch)

    if (matchesAuthor) return true

    const matchesDescription = String(scenario.description)
      .toLowerCase()
      .includes(lowercaseSearch)

    return matchesDescription
  })
}

export const ACTIVE_RUN_STAGES = [
  ScenarioRunStage.SETUP,
  ScenarioRunStage.ACTIVE,
  ScenarioRunStage.HALTING
]
export const ERROR_RUN_STAGES = [
  ScenarioRunStage.SETUP_FAILED,
  ScenarioRunStage.FAILED
]
export const COMPLETED_RUN_STAGES = [
  ScenarioRunStage.HALTED,
  ScenarioRunStage.COMPLETED
]

export const getRunStage = (run?: ScenarioRunResponse) => run?.stage_info?.stage

export const isRunInActiveStage = (run?: ScenarioRunResponse) => {
  const runStage = getRunStage(run)

  return Boolean(runStage && ACTIVE_RUN_STAGES.includes(runStage))
}

export const isRunInCompletedStage = (run: ScenarioRunResponse) => {
  const runStage = getRunStage(run)

  return (
    isRunInErrorStage(run) ||
    Boolean(runStage && COMPLETED_RUN_STAGES.includes(runStage))
  )
}

export const isRunInErrorStage = (run: ScenarioRunResponse) => {
  const runStage = getRunStage(run)

  return Boolean(runStage && ERROR_RUN_STAGES.includes(runStage))
}

export const isRunHalting = (run: ScenarioRunResponse) =>
  getRunStage(run) === 'Halted'

export const hasBeenRun = (scenario: Scenario) => Boolean(scenario.last_run_at)

export const getRunStageMetadata = (run?: ScenarioRunResponse) =>
  run?.stage_info?.stageMetadata

export function createServiceAttackFromForm(
  { target, impact },
  attackConfiguration: AttackConfiguration
) {
  const command = {
    infra_command_type: impact.type,
    infra_command_args: {
      ...attackConfiguration.command.infraCommandArgs,
      cli_args: [
        impact.type,
        ...(impact?.args?.map((arg) => arg.toString()) ?? [])
      ],
      providers: impact.providers,
      trafficImpactMapping: impact.trafficImpactMapping
        ? cleanObjKeysAndValues(impact.trafficImpactMapping, {
            keyTransformers: [snakeCase]
          })
        : undefined
    }
  }

  return {
    attack_configuration: {
      ...keysToSnakeCase(attackConfiguration),
      command
    },
    include_new_targets: Boolean(target.includeNewTargets),
    continue_on_empty_targets: Boolean(impact.continueOnEmptyTargets),
    type: ScenarioGraphNodeType.InfraAttack
  }
}

export function createAttackFromForm(
  targetType: TargetType,
  {
    target,
    impact
  }: {
    target: InfraTargetFormValues
    impact: InfraImpactFormValues
  },
  targets: K8sObject[] = [],
  metadata: Metadata
) {
  const attackConfiguration = generateAttackConfiguration(
    targetType,
    { target, impact },
    metadata,
    targets
  )

  return {
    attack_configuration: attackConfiguration,
    type: ScenarioGraphNodeType.InfraAttack,
    ...('includeNewTargets' in target && {
      include_new_targets: Boolean(target.includeNewTargets)
    }),
    ...('continueOnEmptyTargets' in impact && {
      continue_on_empty_targets: Boolean(impact.continueOnEmptyTargets)
    })
  }
}

export function getStatusCheckDetails(statusCheckNode: StatusCheckNode) {
  const { evaluation_results, lifecycle } = statusCheckNode?.state ?? {}

  if (evaluation_results?.passed) {
    return {
      color: theme.colors.green[0],
      icon: 'solidCheck',
      text: isActive(lifecycle) ? 'Passing' : 'Passed'
    } as const
  }

  return {
    color: theme.colors.red[0],
    icon: 'solidClose',
    text: 'Failed'
  } as const
}
